/**
 * Offline-aware sync engine.
 *
 * Public API (also intended as the RN interface — see /app/mobile/README.md):
 *   pullSnapshot(workspaceId)         — refresh local caches from server
 *   enqueueMutation(workspaceId, m)   — save a mutation to run when online
 *   replayQueue(workspaceId)          — attempt to send all queued mutations
 *   startAutoSync(workspaceId)        — set up online/visibilitychange listeners
 *   getStatus()                       — { online, pending, syncing, last_sync_at }
 *
 * Conflict resolution:
 *   - PATCH mutations use `updated_at` as the base version. If the server row
 *     has a newer `updated_at`, the mutation is rejected with a merged response
 *     stored on the mutation record (status='conflict') for the UI to resolve.
 *   - Idempotency: every mutation carries an `idempotency_key` UUID. The
 *     backend can (later) key on this to safely retry.
 */
import { v4 as uuidv4 } from "uuid";
import api from "@/lib/api";
import { db, setMeta, getMeta } from "./db";

let subscribers = new Set();
let syncing = false;
let currentWorkspace = null;

function emit() {
  const s = getStatus();
  subscribers.forEach((fn) => { try { fn(s); } catch { /* ignore */ } });
}

export function subscribe(fn) { subscribers.add(fn); fn(getStatus()); return () => subscribers.delete(fn); }

export function getStatus() {
  return {
    online: typeof navigator !== "undefined" ? navigator.onLine : true,
    syncing,
    // pending is populated by callers (fast counters via .count())
  };
}

export async function pullSnapshot(workspaceId) {
  if (!workspaceId) return { ok: false };
  try {
    const [prods, cats, customers] = await Promise.all([
      api.get("/m/inventory/products", { params: { limit: 2000 } }),
      api.get("/m/inventory/categories"),
      api.get("/m/crm/customers", { params: { limit: 2000 } }).catch(() => ({ data: { customers: [] } })),
    ]);
    await db.transaction("rw", db.products, db.categories, db.customers, async () => {
      await db.products.where({ workspace_id: workspaceId }).delete();
      await db.categories.where({ workspace_id: workspaceId }).delete();
      await db.customers.where({ workspace_id: workspaceId }).delete();
      await db.products.bulkPut((prods.data.products || []).map((p) => ({ ...p, workspace_id: workspaceId })));
      await db.categories.bulkPut((cats.data.categories || []).map((c) => ({ ...c, workspace_id: workspaceId })));
      await db.customers.bulkPut((customers.data.customers || []).map((c) => ({ ...c, workspace_id: workspaceId })));
    });
    await setMeta(`last_sync_at:${workspaceId}`, new Date().toISOString());
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e?.message };
  }
}

export async function enqueueMutation(workspaceId, { entity, method, url, body, local_id, depends_on }) {
  const mutation = {
    workspace_id: workspaceId,
    entity, method, url, body,
    local_id: local_id || null,
    depends_on: depends_on || null,       // link to mutation.id of a not-yet-synced parent
    idempotency_key: uuidv4(),
    created_at: new Date().toISOString(),
    tries: 0,
    status: "pending",
    error: null,
    server_id: null,
  };
  const id = await db.mutations.add(mutation);
  emit();
  // Try immediately if online
  if (typeof navigator !== "undefined" && navigator.onLine) {
    replayQueue(workspaceId).catch(() => {});
  }
  return { ...mutation, id };
}

async function _executeOne(mut) {
  await db.mutations.update(mut.id, { tries: (mut.tries || 0) + 1, status: "syncing" });
  try {
    const headers = { "Idempotency-Key": mut.idempotency_key };
    let body = mut.body;
    // If this mutation depends on an earlier one, resolve its server_id first
    if (mut.depends_on) {
      const parent = await db.mutations.get(mut.depends_on);
      if (!parent || parent.status !== "success") {
        await db.mutations.update(mut.id, { status: "pending" });
        return { ok: false, defer: true };
      }
      body = JSON.parse(JSON.stringify(body).replace(new RegExp(mut.depends_on_placeholder || "__PARENT__", "g"), parent.server_id));
    }
    const { data } = await api.request({ url: mut.url, method: mut.method, data: body, headers });
    await db.mutations.update(mut.id, {
      status: "success",
      server_id: data?.product_id || data?.customer_id || data?.order_id || data?.id || null,
      completed_at: new Date().toISOString(),
      error: null,
    });
    return { ok: true, data };
  } catch (e) {
    const status = e?.response?.status;
    const isConflict = status === 409;
    await db.mutations.update(mut.id, {
      status: isConflict ? "conflict" : (mut.tries >= 5 ? "failed" : "pending"),
      error: e?.response?.data?.detail || e?.message,
      last_error_at: new Date().toISOString(),
    });
    return { ok: false, error: e };
  }
}

export async function replayQueue(workspaceId) {
  if (syncing || (typeof navigator !== "undefined" && !navigator.onLine)) return;
  syncing = true; emit();
  try {
    const pending = await db.mutations
      .where("workspace_id").equals(workspaceId)
      .and((m) => m.status === "pending")
      .toArray();
    // Sort by created_at to preserve causality
    pending.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    for (const m of pending) {
      await _executeOne(m);
    }
  } finally {
    syncing = false; emit();
  }
}

export async function pendingCount(workspaceId) {
  return db.mutations
    .where("workspace_id").equals(workspaceId)
    .and((m) => m.status === "pending" || m.status === "conflict")
    .count();
}

let listenersInstalled = false;

export function startAutoSync(workspaceId) {
  currentWorkspace = workspaceId;
  if (listenersInstalled) return;
  listenersInstalled = true;
  window.addEventListener("online", () => { if (currentWorkspace) replayQueue(currentWorkspace); });
  window.addEventListener("offline", () => emit());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && navigator.onLine && currentWorkspace) {
      replayQueue(currentWorkspace);
    }
  });
  // Listen for service worker triggers
  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener("message", (e) => {
      if (e.data?.type === "REPLAY_QUEUE" && currentWorkspace) replayQueue(currentWorkspace);
    });
  }
  // Best-effort initial sync
  if (navigator.onLine) replayQueue(workspaceId);
}

export { db, getMeta, setMeta };
